import React, { useState, useEffect, useMemo } from 'react';
import MetaTags from '../components/common/MetaTags';
import './BolaoSbxPage.css';

const BolaoSbxPage = () => {
  // Simulator State
  const [resHome, setResHome] = useState(4);
  const [resAway, setResAway] = useState(2);
  const [tipHome, setTipHome] = useState(3);
  const [tipAway, setTipAway] = useState(1);

  // Intersection Observer for scroll reveal animations
  useEffect(() => {
    const reveals = document.querySelectorAll('.bolao-reveal');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('bolao-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );

    reveals.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // Simulator Points Calculation
  const simulationResult = useMemo(() => {
    const rh = resHome;
    const ra = resAway;
    const th = tipHome;
    const ta = tipAway;

    const resWinner = rh > ra ? 'home' : ra > rh ? 'away' : 'draw';
    const tipWinner = th > ta ? 'home' : ta > th ? 'away' : 'draw';

    let pts = 0;
    let cat = 'zero';
    let catName = 'Sem pontos';
    let catDesc = 'Palpite incorreto';

    if (rh === th && ra === ta) {
      pts = 25;
      cat = 'exact';
      catName = 'Placar exato';
      catDesc = 'Acertou o placar completo';
    } else if (resWinner === tipWinner && resWinner !== 'draw') {
      const winGoalsRes = resWinner === 'home' ? rh : ra;
      const loseGoalsRes = resWinner === 'home' ? ra : rh;
      const winGoalsTip = tipWinner === 'home' ? th : ta;
      const loseGoalsTip = tipWinner === 'home' ? ta : th;
      const diffRes = Math.abs(rh - ra);
      const diffTip = Math.abs(th - ta);

      if (winGoalsRes === winGoalsTip) {
        pts = 18;
        cat = 'winner-goals';
        catName = 'Gols do vencedor';
        catDesc = 'Vencedor certo + gols do vencedor correto';
      } else if (diffRes === diffTip) {
        pts = 15;
        cat = 'goal-diff';
        catName = 'Saldo de gols';
        catDesc = 'Vencedor certo + saldo de gols correto';
      } else if (loseGoalsRes === loseGoalsTip) {
        pts = 12;
        cat = 'loser-goals';
        catName = 'Gols do perdedor';
        catDesc = 'Vencedor certo + gols do perdedor correto';
      } else {
        pts = 10;
        cat = 'winner-only';
        catName = 'Vencedor certo';
        catDesc = 'Acertou apenas o vencedor da partida';
      }
    } else if (resWinner === 'draw' && tipWinner === 'draw') {
      const diffRes = rh - ra; // 0
      const diffTip = th - ta; // 0
      if (diffRes === diffTip) {
        pts = 15;
        cat = 'goal-diff';
        catName = 'Saldo de gols';
        catDesc = 'Empate certo + saldo de gols correto';
      } else {
        pts = 10;
        cat = 'winner-only';
        catName = 'Vencedor certo';
        catDesc = 'Acertou o empate';
      }
    }

    return { pts, cat, catName, catDesc };
  }, [resHome, resAway, tipHome, tipAway]);

  const handleScoreChange = (type, action) => {
    if (type === 'res-home') {
      setResHome((prev) => {
        const next = prev + action;
        return next < 0 ? 0 : next > 9 ? 9 : next;
      });
    } else if (type === 'res-away') {
      setResAway((prev) => {
        const next = prev + action;
        return next < 0 ? 0 : next > 9 ? 9 : next;
      });
    } else if (type === 'tip-home') {
      setTipHome((prev) => {
        const next = prev + action;
        return next < 0 ? 0 : next > 9 ? 9 : next;
      });
    } else if (type === 'tip-away') {
      setTipAway((prev) => {
        const next = prev + action;
        return next < 0 ? 0 : next > 9 ? 9 : next;
      });
    }
  };

  // Smooth scroll handler for anchor links
  const handleAnchorClick = (e, targetId) => {
    e.preventDefault();
    const targetElement = document.getElementById(targetId);
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="bolao-page">
      <MetaTags
        title="Bolão SBX | Copa do Mundo 2026"
        description="Participe do Bolão SBX da Copa do Mundo 2026. Acerte os placares dos jogos, some pontos e concorra a prêmios incríveis como R$1.000 no Pix, Hub Presencial ou Comunidade SBX."
        keywords="bolao copa do mundo, bolao sbx, palpites copa do mundo, day trade, copa do mundo 2026, sbx trade academy"
        image="https://cms.sbxtreinamentos.com.br/wp-content/uploads/2025/10/hero-image-2.png"
        url="https://sbxtreinamentos.com.br/bolao-sbx"
        type="website"
        author="Eduardo SBX"
        siteName="SBX Trade Academy"
      />

      {/* NAV */}
      <nav className="bolao-nav">
        <a href="#hero" className="bolao-nav-logo" onClick={(e) => handleAnchorClick(e, 'bolao-hero')}>
          <img src="/logo-sbx.png" alt="SBX Trade Logo" />
          <span>· Bolão 2026</span>
        </a>
        <ul className="bolao-nav-links">
          <li>
            <a href="#premios" onClick={(e) => handleAnchorClick(e, 'premios')}>
              Prêmios
            </a>
          </li>
          <li>
            <a href="#participar" onClick={(e) => handleAnchorClick(e, 'participar')}>
              Participar
            </a>
          </li>
          <li>
            <a href="#regras" onClick={(e) => handleAnchorClick(e, 'regras')}>
              Regras
            </a>
          </li>
          <li>
            <a href="#simulador" onClick={(e) => handleAnchorClick(e, 'simulador')}>
              Simulador
            </a>
          </li>
        </ul>
      </nav>

      {/* HERO */}
      <section id="bolao-hero" className="bolao-hero">
        <img className="bolao-hero-logo" src="/logo-sbx.png" alt="SBX Logo" />

        <p className="bolao-hero-eyebrow">
          <span />
          Copa do Mundo 2026
          <span />
        </p>

        <h1 className="bolao-hero-title">
          Participe
          <br />
          do <em>Bolão</em>
          <br />
          SBX
        </h1>

        <h2 className="bolao-hero-subtitle">Acha que consegue acertar?</h2>

        <div className="bolao-hero-cta">
          <a
            href="#participar"
            className="bolao-btn-primary"
            onClick={(e) => handleAnchorClick(e, 'participar')}
          >
            Participar agora
          </a>
        </div>
      </section>

      {/* TICKER */}
      <div className="bolao-ticker" aria-hidden="true">
        <div className="bolao-ticker-inner">
          <span>Copa do Mundo 2026</span>
          <span className="dot">✦</span>
          <span>Bolão SBX</span>
          <span className="dot">✦</span>
          <span>R$1.000 no pix</span>
          <span className="dot">✦</span>
          <span>Acerte o placar</span>
          <span className="dot">✦</span>
          <span>Pontos dobrados no mata-mata</span>
          <span className="dot">✦</span>
          <span>Copa do Mundo 2026</span>
          <span className="dot">✦</span>
          <span>Bolão SBX</span>
          <span className="dot">✦</span>
          <span>R$1.000 no pix</span>
          <span className="dot">✦</span>
          <span>Acerte o placar</span>
          <span className="dot">✦</span>
          <span>Pontos dobrados no mata-mata</span>
          <span className="dot">✦</span>
        </div>
      </div>

      {/* PRÊMIOS */}
      <section id="premios" className="bolao-section bolao-premios">
        <div className="bolao-premios-inner">
          <div className="bolao-premios-header bolao-reveal">
            <div className="bolao-copa-flag">
              <span className="bolao-flag-green" />
              <span className="bolao-flag-yellow" />
              <span className="bolao-flag-blue" />
              <span className="bolao-flag-white" />
              <span className="bolao-flag-green" />
            </div>
            <p className="bolao-section-label">O que está em jogo</p>
            <h2>
              Quem <span>acerta</span>,
              <br />
              leva tudo
            </h2>
            <p>O vencedor do Bolão SBX escolhe um dos prêmios abaixo</p>
          </div>

          <div className="bolao-premios-grid bolao-reveal">
            <div className="bolao-premio-card">
              <span className="bolao-premio-icon">💸</span>
              <div className="bolao-premio-value">R$1.000</div>
              <div className="bolao-premio-label">no PIX</div>
              <p className="bolao-premio-desc">
                Dinheiro direto na sua conta, sem enrolação. Instantâneo.
              </p>
              <span className="bolao-premio-badge">Opção 1</span>
            </div>

            <div className="bolao-premio-card">
              <span className="bolao-premio-icon">🏛️</span>
              <div className="bolao-premio-oldval">R$ 2.000,00</div>
              <div className="bolao-premio-value">1 Mês</div>
              <div className="bolao-premio-label">HUB Presencial</div>
              <p className="bolao-premio-desc">
                Acesso completo ao HUB presencial SBX por 30 dias. Aprenda ao lado dos melhores.
              </p>
              <span className="bolao-premio-badge">Opção 2</span>
            </div>

            <div className="bolao-premio-card">
              <span className="bolao-premio-icon">🌐</span>
              <div className="bolao-premio-oldval">R$ 4.000,00</div>
              <div className="bolao-premio-value">1 Ano</div>
              <div className="bolao-premio-label">Comunidade SBX</div>
              <p className="bolao-premio-desc">
                12 meses de acesso à comunidade exclusiva. Conteúdo, mentorias e network.
              </p>
              <span className="bolao-premio-badge">Opção 3</span>
            </div>
          </div>
        </div>
      </section>

      {/* COMO PARTICIPAR */}
      <section id="participar" className="bolao-section bolao-participar">
        <div className="bolao-participar-inner">
          <div className="bolao-participar-header bolao-reveal">
            <p className="bolao-section-label">Passo a passo</p>
            <h2>
              Como
              <br />
              participar
            </h2>
          </div>

          <div className="bolao-steps">
            <div className="bolao-step bolao-reveal">
              <div className="bolao-step-num">01</div>
              <div className="bolao-step-content">
                <h3>Entre no grupo do WhatsApp</h3>
                <p>Receba atualizações, lembretes dos jogos e informações do bolão em primeira mão.</p>
              </div>
              <a
                href="https://chat.whatsapp.com/J16ADKO1N6XD1MNoeL1XJd"
                target="_blank"
                rel="noopener noreferrer"
                className="bolao-btn-whatsapp"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                Entrar no grupo
              </a>
            </div>

            <div className="bolao-step bolao-reveal">
              <div className="bolao-step-num">02</div>
              <div className="bolao-step-content">
                <h3>Crie sua conta no app do bolão</h3>
                <p>Acesse o DaCopa e registre sua conta gratuitamente. É onde você vai fazer seus palpites.</p>
              </div>
              <a
                href="https://www.dacopa.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="bolao-btn-outline"
              >
                Criar Conta
              </a>
            </div>

            <div className="bolao-step bolao-reveal">
              <div className="bolao-step-num">03</div>
              <div className="bolao-step-content">
                <h3>Entre no Bolão SBX dentro do app</h3>
                <p>Após criar sua conta, pesquise pelo Bolão SBX no app e entre para competir com a turma.</p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                <span
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: 'var(--bolao-gray-dark)',
                  }}
                >
                  No app
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* REGRAS */}
      <section id="regras" className="bolao-section bolao-regras">
        <div className="bolao-regras-inner">
          <div className="bolao-regras-header bolao-reveal">
            <p className="bolao-section-label">Tudo que você precisa saber</p>
            <h2>
              Regras
              <br />
              do jogo
            </h2>
          </div>

          <div className="bolao-regras-grid bolao-reveal">
            <div className="bolao-regra-item">
              <p className="bolao-regra-num">Regra 01</p>
              <h3>Palpites</h3>
              <p>
                Faça seu palpite do placar de cada jogo antes do início da partida. Você pode alterar seu palpite
                até o apito inicial.
              </p>
            </div>

            <div className="bolao-regra-item">
              <p className="bolao-regra-num">Regra 02</p>
              <h3>Pontuação</h3>
              <p>
                Sistema de 6 níveis: de 25 pontos pelo placar exato até 0 por errar o palpite. Quanto mais perto do
                resultado, mais pontos você ganha!
              </p>
            </div>

            <div className="bolao-regra-item">
              <p className="bolao-regra-num">Regra 03</p>
              <h3>Pontos dobrados × 2</h3>
              <p>
                Na Copa do Mundo 2026, as partidas do mata-mata valem o dobro. Acertar um placar exato vale 50 pontos,
                não 25. Procure pelo selo ×2 nos cards das partidas.
              </p>
            </div>

            <div className="bolao-regra-item">
              <p className="bolao-regra-num">Regra 04</p>
              <h3>Prorrogação</h3>
              <p>
                Se a partida for para a prorrogação, o placar final considerado será o do fim da prorrogação (90min +
                30min). Pênaltis não contam.
              </p>
            </div>

            <div className="bolao-regra-item">
              <p className="bolao-regra-num">Regra 05</p>
              <h3>Prazo dos palpites</h3>
              <p>
                Os palpites são bloqueados no horário previsto do jogo ou no pontapé inicial, o que ocorrer primeiro.
                Fique atento aos horários dos jogos!
              </p>
            </div>

            <div className="bolao-regra-item">
              <p className="bolao-regra-num">Regra 06</p>
              <h3>Ranking e desempate</h3>
              <p>
                Os pontos de todos os jogos são somados. Em caso de empate: mais placares exatos → acertos de vencedor
                + gols → saldo de gols → gols do perdedor → vencedor certo → quem entrou primeiro.
              </p>
            </div>

            <div className="bolao-regra-item">
              <p className="bolao-regra-num">Regra 07</p>
              <h3>Grupo do WhatsApp</h3>
              <p>
                É obrigatório estar no grupo oficial do WhatsApp do Bolão SBX para validar sua participação e ser elegível para receber qualquer prêmio.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SIMULADOR */}
      <section id="simulador" className="bolao-section bolao-simulador">
        <div className="bolao-sim-inner">
          <div className="bolao-sim-header bolao-reveal">
            <p className="bolao-section-label">Entenda a pontuação</p>
            <h2>
              Simulador de
              <br />
              pontuação
            </h2>
            <p>Ajuste o resultado e o seu palpite para ver quantos pontos você recebe</p>
          </div>

          <div className="bolao-sim-box bolao-reveal">
            {/* Painel esquerdo: inputs */}
            <div className="bolao-sim-panel">
              <div className="bolao-score-adjusters">
                <div className="bolao-score-adjuster-item">
                  <h3>Resultado da partida</h3>
                  <div className="bolao-score-display">
                    <div className="bolao-team-score">
                      <span className="bolao-team-name">Casa</span>
                      <span className="bolao-score-num">{resHome}</span>
                    </div>
                    <div className="bolao-score-controls">
                      <button className="bolao-score-btn" onClick={() => handleScoreChange('res-home', 1)}>
                        ▲
                      </button>
                      <button className="bolao-score-btn" onClick={() => handleScoreChange('res-home', -1)}>
                        ▼
                      </button>
                    </div>
                    <span className="bolao-score-sep">:</span>
                    <div className="bolao-score-controls">
                      <button className="bolao-score-btn" onClick={() => handleScoreChange('res-away', 1)}>
                        ▲
                      </button>
                      <button className="bolao-score-btn" onClick={() => handleScoreChange('res-away', -1)}>
                        ▼
                      </button>
                    </div>
                    <div className="bolao-team-score">
                      <span className="bolao-team-name">Fora</span>
                      <span className="bolao-score-num">{resAway}</span>
                    </div>
                  </div>
                </div>

                <div className="bolao-score-adjuster-item">
                  <h3>Seu palpite</h3>
                  <div className="bolao-score-display">
                    <div className="bolao-team-score">
                      <span className="bolao-team-name">Casa</span>
                      <span className="bolao-score-num">{tipHome}</span>
                    </div>
                    <div className="bolao-score-controls">
                      <button className="bolao-score-btn" onClick={() => handleScoreChange('tip-home', 1)}>
                        ▲
                      </button>
                      <button className="bolao-score-btn" onClick={() => handleScoreChange('tip-home', -1)}>
                        ▼
                      </button>
                    </div>
                    <span className="bolao-score-sep">:</span>
                    <div className="bolao-score-controls">
                      <button className="bolao-score-btn" onClick={() => handleScoreChange('tip-away', 1)}>
                        ▲
                      </button>
                      <button className="bolao-score-btn" onClick={() => handleScoreChange('tip-away', -1)}>
                        ▼
                      </button>
                    </div>
                    <div className="bolao-team-score">
                      <span className="bolao-team-name">Fora</span>
                      <span className="bolao-score-num">{tipAway}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className={`bolao-result-display ${
                  simulationResult.pts === 25
                    ? 'exact-points'
                    : simulationResult.pts > 0
                    ? 'has-points'
                    : ''
                }`}
              >
                <div className="bolao-result-pts">{simulationResult.pts}</div>
                <div className="bolao-result-label">pontos</div>
                <div className="bolao-result-category">{simulationResult.catName}</div>
                <div className="bolao-result-desc">{simulationResult.catDesc}</div>
              </div>
            </div>

            {/* Painel direito: tabela de pontuação */}
            <div className="bolao-sim-panel">
              <h3>Tabela de pontuação</h3>
              <div className="bolao-sim-table">
                <div
                  className={`bolao-sim-table-row ${
                    simulationResult.cat === 'exact' ? 'active' : 'inactive'
                  }`}
                >
                  <div className="bolao-row-left">
                    <span className="bolao-row-name">Placar exato</span>
                    <span className="bolao-row-sub">Acertou o placar completo</span>
                  </div>
                  <span className="bolao-row-pts">25 pts</span>
                </div>

                <div
                  className={`bolao-sim-table-row ${
                    simulationResult.cat === 'winner-goals' ? 'active' : 'inactive'
                  }`}
                >
                  <div className="bolao-row-left">
                    <span className="bolao-row-name">Gols do vencedor</span>
                    <span className="bolao-row-sub">Vencedor certo + gols do vencedor</span>
                  </div>
                  <span className="bolao-row-pts">18 pts</span>
                </div>

                <div
                  className={`bolao-sim-table-row ${
                    simulationResult.cat === 'goal-diff' ? 'active' : 'inactive'
                  }`}
                >
                  <div className="bolao-row-left">
                    <span className="bolao-row-name">Saldo de gols</span>
                    <span className="bolao-row-sub">Vencedor/empate + saldo correto</span>
                  </div>
                  <span className="bolao-row-pts">15 pts</span>
                </div>

                <div
                  className={`bolao-sim-table-row ${
                    simulationResult.cat === 'loser-goals' ? 'active' : 'inactive'
                  }`}
                >
                  <div className="bolao-row-left">
                    <span className="bolao-row-name">Gols do perdedor</span>
                    <span className="bolao-row-sub">Vencedor certo + gols do perdedor</span>
                  </div>
                  <span className="bolao-row-pts">12 pts</span>
                </div>

                <div
                  className={`bolao-sim-table-row ${
                    simulationResult.cat === 'winner-only' ? 'active' : 'inactive'
                  }`}
                >
                  <div className="bolao-row-left">
                    <span className="bolao-row-name">Vencedor certo</span>
                    <span className="bolao-row-sub">Acertou apenas o vencedor</span>
                  </div>
                  <span className="bolao-row-pts">10 pts</span>
                </div>

                <div
                  className={`bolao-sim-table-row ${
                    simulationResult.cat === 'zero' ? 'active' : 'inactive'
                  }`}
                >
                  <div className="bolao-row-left">
                    <span className="bolao-row-name">Sem pontos</span>
                    <span className="bolao-row-sub">Palpite incorreto</span>
                  </div>
                  <span className="bolao-row-pts">0 pts</span>
                </div>
              </div>

              <div className="bolao-sim-banner">
                <p>
                  <strong>× 2 MATA-MATA</strong>
                  <br />
                  Na Copa do Mundo 2026, as partidas do mata-mata valem o dobro. Procure pelo selo ×2 nos cards.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="bolao-cta-final">
        <div className="bolao-cta-final-inner">
          <div className="bolao-copa-flag" style={{ justifyContent: 'center', marginBottom: '1.5rem' }}>
            <span className="bolao-flag-green" />
            <span className="bolao-flag-yellow" />
            <span className="bolao-flag-blue" />
            <span className="bolao-flag-white" />
            <span className="bolao-flag-green" />
          </div>
          <p className="bolao-section-label bolao-reveal">Copa do Mundo 2026</p>
          <h2 className="bolao-reveal">
            Está pronto para
            <br />
            fazer seu palpite?
          </h2>
          <p className="bolao-reveal">Junte-se aos alunos SBX e dispute o prêmio. Cada jogo é uma chance.</p>
          <div className="bolao-reveal">
            <a
              href="#participar"
              className="bolao-btn-primary"
              onClick={(e) => handleAnchorClick(e, 'participar')}
            >
              Começar agora
            </a>
          </div>
        </div>
      </section>

    </div>
  );
};

export default BolaoSbxPage;
